import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend
} from 'recharts';
import { UsersIcon, DocumentArrowUpIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const StatCard = ({ label, value, Icon, bg, fg }) => (
  <div className="bg-white rounded-2xl shadow p-5 flex items-center gap-4">
    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${bg}`}>
      <Icon className={`h-6 w-6 ${fg}`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{Number(value || 0).toLocaleString()}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

const BarPanel = ({ title, data, color }) => (
  <div className="bg-white rounded-2xl shadow p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
    {(!data || data.length === 0) ? (
      <p className="text-sm text-gray-400">No data yet</p>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 34)}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const UserAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getUserAnalytics();
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-gray-500">Loading analytics…</div>;
  if (!data) return <div className="p-8 text-gray-500">No analytics available.</div>;

  const {
    totals = {}, byDegree = [], byJob = [], byState = [], byAge = [],
    perUser = [], activity = []
  } = data;

  const activityFmt = activity.map((a) => ({
    d: String(a.d).slice(5, 10),
    signups: Number(a.signups),
    uploads: Number(a.uploads),
  }));

  return (
    <div className="space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Users" value={totals.users} Icon={UsersIcon} bg="bg-blue-100" fg="text-blue-600" />
        <StatCard label="Uploads" value={totals.uploads} Icon={DocumentArrowUpIcon} bg="bg-green-100" fg="text-green-600" />
        <StatCard label="Chats" value={totals.chats} Icon={ChatBubbleLeftRightIcon} bg="bg-purple-100" fg="text-purple-600" />
      </div>

      {/* Demographic breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarPanel title="Users by job role" data={byJob} color="#2563eb" />
        <BarPanel title="Users by degree" data={byDegree} color="#16a34a" />
        <BarPanel title="Users by state" data={byState} color="#7c3aed" />
        <BarPanel title="Users by age" data={byAge} color="#ea580c" />
      </div>

      {/* Activity over time */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Signups &amp; uploads (last 30 days)</h3>
        {activityFmt.length === 0 ? (
          <p className="text-sm text-gray-400">No recent activity</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={activityFmt} margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="d" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="signups" stroke="#2563eb" name="Signups" />
              <Line type="monotone" dataKey="uploads" stroke="#16a34a" name="Uploads" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-user table */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Per-user activity</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">User</th>
                <th className="py-2 pr-4">Degree</th>
                <th className="py-2 pr-4">Job role</th>
                <th className="py-2 pr-4">State</th>
                <th className="py-2 pr-4 text-right">Uploads</th>
                <th className="py-2 pr-4 text-right">Chats</th>
              </tr>
            </thead>
            <tbody>
              {perUser.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-900">{u.username}</div>
                    <div className="text-xs text-gray-400">{u.email}</div>
                  </td>
                  <td className="py-2 pr-4">{u.degree || '—'}</td>
                  <td className="py-2 pr-4">{u.job_role || '—'}</td>
                  <td className="py-2 pr-4">{u.region || '—'}</td>
                  <td className="py-2 pr-4 text-right">{u.uploads}</td>
                  <td className="py-2 pr-4 text-right">{u.chats}</td>
                </tr>
              ))}
              {perUser.length === 0 && (
                <tr><td colSpan="6" className="py-4 text-center text-gray-400">No users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;
